// Unified facade over Yandex.Metrica and Google Analytics 4.
//
// Behaviour:
//   * Counter IDs come from env: REACT_APP_YM_ID and REACT_APP_GA_ID.
//   * If a counter ID is missing → that backend is silently disabled.
//   * Scripts are loaded lazily on consent (see CookieBanner).
//   * Events fired before init/consent are queued and flushed after init.
//   * All page-view and event tracking must go through this module — do NOT
//     reference window.ym / window.gtag directly elsewhere.

const CONSENT_KEY = 'cookie_consent';
const STORAGE_USER_KEY = 'analytics_user_id';

const YM_ID  = process.env.REACT_APP_YM_ID  || '';
const GA_ID  = process.env.REACT_APP_GA_ID  || '';
const WEBVISOR = (process.env.REACT_APP_YM_WEBVISOR || 'true').toLowerCase() === 'true';

type EventParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

interface QueueItem {
  kind: 'event' | 'pageview' | 'userId';
  name?: string;
  path?: string;
  userId?: string | number | null;
  params?: EventParams;
}

let consentGranted = false;
let ymReady = false;
let gaReady = false;
const queue: QueueItem[] = [];

const hasConsent = (): boolean => {
  try { return localStorage.getItem(CONSENT_KEY) === 'accepted'; }
  catch { return false; }
};

const injectScript = (src: string, async = true): Promise<void> =>
  new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = async;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });

// ── Yandex.Metrica loader ───────────────────────────────────────────────────
const initYandex = async (): Promise<void> => {
  if (!YM_ID || ymReady) return;
  // Stub queue per official snippet
  (function (m: any, e: any, t: any, r: any, i: any) {
    m[i] = m[i] || function () { (m[i].a = m[i].a || []).push(arguments); };
    m[i].l = 1 * (new Date() as any);
  })(window, document, 'script', '', 'ym');

  try {
    await injectScript('https://mc.yandex.ru/metrika/tag.js');
    window.ym?.(Number(YM_ID), 'init', {
      clickmap     : true,
      trackLinks   : true,
      accurateTrackBounce: true,
      webvisor     : WEBVISOR,
      defer        : true,
      ecommerce    : 'dataLayer',
      // First-party cookies only.
      // childIframe / trackHash are intentionally left default.
    });
    ymReady = true;
  } catch (err) {
    // Silently fail — analytics should never break the app
    // eslint-disable-next-line no-console
    console.warn('[analytics] Yandex.Metrica init failed', err);
  }
};

// ── Google Analytics 4 loader ───────────────────────────────────────────────
const initGoogle = async (): Promise<void> => {
  if (!GA_ID || gaReady) return;
  window.dataLayer = window.dataLayer || [];
  // Define gtag stub before script loads
  window.gtag = function gtag(...args: unknown[]) {
    (window.dataLayer as unknown[]).push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: false, // we send page_view manually on Router change
    anonymize_ip  : true,
  });

  try {
    await injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`);
    gaReady = true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] Google Analytics init failed', err);
  }
};

const flushQueue = () => {
  while (queue.length > 0) {
    const item = queue.shift()!;
    if (item.kind === 'event' && item.name) {
      sendEvent(item.name, item.params);
    } else if (item.kind === 'pageview' && item.path) {
      sendPageView(item.path);
    } else if (item.kind === 'userId') {
      sendUserId(item.userId ?? null);
    }
  }
};

const sendEvent = (name: string, params?: EventParams) => {
  if (ymReady && YM_ID) {
    try { window.ym?.(Number(YM_ID), 'reachGoal', name, params ?? {}); } catch { /* noop */ }
  }
  if (gaReady && GA_ID) {
    try { window.gtag?.('event', name, params ?? {}); } catch { /* noop */ }
  }
};

const sendPageView = (path: string) => {
  if (ymReady && YM_ID) {
    try { window.ym?.(Number(YM_ID), 'hit', window.location.origin + path, { title: document.title }); } catch { /* noop */ }
  }
  if (gaReady && GA_ID) {
    try {
      window.gtag?.('event', 'page_view', {
        page_path    : path,
        page_title   : document.title,
        page_location: window.location.origin + path,
      });
    } catch { /* noop */ }
  }
};

const sendUserId = (userId: string | number | null) => {
  if (ymReady && YM_ID) {
    try {
      if (userId !== null) window.ym?.(Number(YM_ID), 'setUserID', String(userId));
      window.ym?.(Number(YM_ID), 'userParams', { UserID: userId ?? undefined });
    } catch { /* noop */ }
  }
  if (gaReady && GA_ID) {
    try {
      window.gtag?.('config', GA_ID, { user_id: userId ? String(userId) : undefined });
    } catch { /* noop */ }
  }
};

// ── Public API ──────────────────────────────────────────────────────────────

/** Call once on app start. Initialises providers only if user consent is stored. */
export const initAnalytics = async (): Promise<void> => {
  if (hasConsent()) {
    await setConsent(true);
  }
};

/** Mark consent granted/revoked. Granting boots scripts and flushes the queue. */
export const setConsent = async (granted: boolean): Promise<void> => {
  consentGranted = granted;
  if (!granted) return;
  await Promise.all([initYandex(), initGoogle()]);
  // Re-emit any user-id stashed before init
  const stashed = (() => { try { return localStorage.getItem(STORAGE_USER_KEY); } catch { return null; } })();
  if (stashed) sendUserId(stashed);
  flushQueue();
};

/** Fire a custom event ("goal" in YM / "event" in GA). */
export const trackEvent = (name: string, params?: EventParams): void => {
  if (!consentGranted || (!ymReady && !gaReady)) {
    queue.push({ kind: 'event', name, params });
    return;
  }
  sendEvent(name, params);
};

/** Record a SPA page view. Wire to React Router via useAnalytics. */
export const trackPageView = (path: string): void => {
  if (!consentGranted || (!ymReady && !gaReady)) {
    queue.push({ kind: 'pageview', path });
    return;
  }
  sendPageView(path);
};

/** Associate the current session with a user (after login). */
export const setAnalyticsUser = (userId: string | number | null): void => {
  try {
    if (userId !== null) localStorage.setItem(STORAGE_USER_KEY, String(userId));
    else localStorage.removeItem(STORAGE_USER_KEY);
  } catch { /* noop */ }
  if (!consentGranted || (!ymReady && !gaReady)) {
    queue.push({ kind: 'userId', userId });
    return;
  }
  sendUserId(userId);
};

/** Convenience: derive a stable user id from a JWT once login succeeds. */
export const setAnalyticsUserFromToken = (token: string | null): void => {
  if (!token) { setAnalyticsUser(null); return; }
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const id = payload.user_id ?? payload.sub ?? payload.id;
    if (id !== undefined && id !== null) setAnalyticsUser(id);
  } catch { /* noop */ }
};
